import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { CallMemo } from '@work4all/models/lib/Classes/CallMemo.entity';
import { CheckList } from '@work4all/models/lib/Classes/CheckList.entity';
import { Document } from '@work4all/models/lib/Classes/Document.entity';
import { EMail } from '@work4all/models/lib/Classes/EMail.entity';
import { Letter } from '@work4all/models/lib/Classes/Letter.entity';
import { Note } from '@work4all/models/lib/Classes/Note.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { SalesOpportunities } from '@work4all/models/lib/Classes/SalesOpportunities.entity';
import { Task } from '@work4all/models/lib/Classes/Task.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  EntityRecordMatcherConfigsByTargetEntity,
  TargetEntityRecordMatcherConfigs,
} from '../types';

import {
  BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
  CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
  PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
  PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
  TOPIC_SIMPLE_ENTITY_RECORD_MATCHER_FIELDS,
} from './entity-record-matcher-common-fields';

const TICKET_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Ticket<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
    title: null,
    problemDescription: null,
  },
  fieldsMappingBySourceEntity: {
    [Entities.callMemo]: {
      problemDescription: 'note',
    },
    [Entities.note]: {
      problemDescription: 'note',
    },
  },
};

const EMAIL_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  EMail<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    topicMarkList: [TOPIC_SIMPLE_ENTITY_RECORD_MATCHER_FIELDS],
  },
};

const APPOINTMENT_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Appointment<EMode.query>
> = {
  fields: {
    id: null,
    title: null,
    note: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
    topicMarkList: [TOPIC_SIMPLE_ENTITY_RECORD_MATCHER_FIELDS],
  },
  fieldsMappingBySourceEntity: {
    [Entities.eMail]: {
      title: 'subject',
      note: 'body',
    },
  },
  restrictedFieldsToSourceEntities: {
    title: [
      Entities.eMail,
      Entities.task,
      Entities.note,
      Entities.callMemo,
      Entities.checkList,
    ],
    note: [Entities.eMail, Entities.task, Entities.note, Entities.callMemo],
  },
};

const CHECK_LIST_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  CheckList<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
  },
};

const NOTE_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Note<EMode.query>
> = {
  fields: {
    id: null,
    title: null,
    note: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
    topicMarkList: [TOPIC_SIMPLE_ENTITY_RECORD_MATCHER_FIELDS],
  },
  fieldsMappingBySourceEntity: {
    [Entities.eMail]: {
      title: 'subject',
      note: 'body',
    },
  },
  restrictedFieldsToSourceEntities: {
    title: [
      Entities.eMail,
      Entities.task,
      Entities.callMemo,
      Entities.checkList,
      Entities.appointment,
    ],
    note: [
      Entities.eMail,
      Entities.task,
      Entities.callMemo,
      Entities.appointment,
    ],
  },
};

const TASK_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Task<EMode.query>
> = {
  fields: {
    id: null,
    title: null,
    note: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
  },
  fieldsMappingBySourceEntity: {
    [Entities.eMail]: {
      title: 'subject',
      note: 'body',
    },
  },
  restrictedFieldsToSourceEntities: {
    title: [
      Entities.eMail,
      Entities.callMemo,
      Entities.note,
      Entities.checkList,
      Entities.appointment,
    ],
    note: [
      Entities.eMail,
      Entities.callMemo,
      Entities.note,
      Entities.appointment,
    ],
  },
};

const CALL_MEMO_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  CallMemo<EMode.query>
> = {
  fields: {
    id: null,
    title: null,
    note: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
  },
  fieldsMappingBySourceEntity: {
    [Entities.eMail]: {
      title: 'subject',
      note: 'body',
    },
  },
  restrictedFieldsToSourceEntities: {
    title: [
      Entities.eMail,
      Entities.task,
      Entities.note,
      Entities.appointment,
      Entities.checkList,
    ],
    note: [Entities.eMail, Entities.task, Entities.note, Entities.appointment],
  },
};

const DOCUMENT_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Document<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    topicMarkList: [TOPIC_SIMPLE_ENTITY_RECORD_MATCHER_FIELDS],
  },
};

const SALES_OPPORTUNITY_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  SalesOpportunities<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
    projectProcess: PROJECT_PROCESS_ENTITY_RECORD_MATCHER_FIELDS,
  },
};

const PROJECT_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Project<EMode.query>
> = {
  fields: {
    id: null,
    ...BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS.data,
  },
};

const LETTER_ENTITY_RECORD_MATCHER_CONFIGS: TargetEntityRecordMatcherConfigs<
  Letter<EMode.query>
> = {
  fields: {
    id: null,
    businessPartner: BUSINESS_PARTNER_ENTITY_RECORD_MATCHER_FIELDS,
    project: PROJECT_ENTITY_RECORD_MATCHER_FIELDS,
    contact: CONTACT_ENTITY_RECORD_MATCHER_FIELDS,
  },
};

export const ENTITY_RECORD_MATCHER_CONFIGS_BY_TARGET_ENTITY: EntityRecordMatcherConfigsByTargetEntity =
  {
    [Entities.ticket]: TICKET_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.eMail]: EMAIL_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.appointment]: APPOINTMENT_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.checkList]: CHECK_LIST_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.note]: NOTE_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.task]: TASK_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.callMemo]: CALL_MEMO_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.document]: DOCUMENT_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.salesOpportunities]:
      SALES_OPPORTUNITY_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.project]: PROJECT_ENTITY_RECORD_MATCHER_CONFIGS,
    [Entities.letter]: LETTER_ENTITY_RECORD_MATCHER_CONFIGS,
  };
